import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import config from "../config";
import Swal from "sweetalert2";
import {
  User, Mail, Shield, Calendar, CheckCircle, XCircle,
  Edit2, Camera, Lock, Save, X, Image as ImageIcon,
  Users, Clock, Award
} from "lucide-react";

const createImage = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
  });

const getCroppedImg = async (imageSrc, crop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = crop.width;
  canvas.height = crop.height;
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg"));
};

const ViewProfiles = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [coverPicUrl, setCoverPicUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("about");

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [coverSrc, setCoverSrc] = useState(null);
  const [coverCrop, setCoverCrop] = useState({ x: 0, y: 0 });
  const [coverZoom, setCoverZoom] = useState(1);
  const [coverCroppedPixels, setCoverCroppedPixels] = useState(null);
  const [showCoverCrop, setShowCoverCrop] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("admin");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setEditData(parsed);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user?.username) return;
    const fetchProfilePicture = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/Admin/get-profile-picture/${user.username}`);
        if (res.ok) setProfilePicUrl(URL.createObjectURL(await res.blob()));
      } catch (err) {}
    };
    const fetchCoverPicture = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/Admin/get-cover-picture/${user.username}`);
        if (res.ok) setCoverPicUrl(URL.createObjectURL(await res.blob()));
      } catch (err) {}
    };
    fetchProfilePicture();
    fetchCoverPicture();
  }, [user?.username]);

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword)
      return Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please fill in both password fields." });
    if (newPassword !== confirmPassword)
      return Swal.fire({ icon: "error", title: "Passwords Don't Match", text: "Make sure both passwords are the same." });
    if (newPassword.length < 6)
      return Swal.fire({ icon: "warning", title: "Too Short", text: "Password must be at least 6 characters." });

    Swal.fire({ title: "Changing Password...", allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
    try {
      const res = await fetch(`${config.API_BASE_URL}/Admin/change-password/${user.username}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword, confirmPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");
      await Swal.fire({ icon: "success", title: "Password Changed!", timer: 2000 });
      setShowPasswordModal(false);
      setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    }
  };

  const handleSaveProfile = async () => {
    Swal.fire({ title: "Updating Profile...", allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
    try {
      const updateRes = await fetch(`${config.API_BASE_URL}/Admin/update-profile/${user.username}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullNames: editData.fullNames, email: editData.email, team: editData.team, roleTeams: editData.roleTeams }),
      });
      if (!updateRes.ok) { const e = await updateRes.json(); throw new Error(e.message); }
      if (imageSrc && croppedAreaPixels) {
        const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
        const fd = new FormData();
        fd.append("file", blob, "profile.png");
        const picRes = await fetch(`${config.API_BASE_URL}/Admin/upload-profile-picture?username=${user.username}`, { method: "POST", body: fd });
        if (!picRes.ok) { const e = await picRes.json(); throw new Error(e.message); }
      }
      localStorage.setItem("admin", JSON.stringify({ ...user, ...editData }));
      await Swal.fire({ icon: "success", title: "Profile Updated!", timer: 1800 });
      window.location.reload();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Update Failed", text: err.message });
    }
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCoverSrc(reader.result); setShowCoverCrop(true); };
    reader.readAsDataURL(file);
  };

  const handleSaveCover = async () => {
    Swal.fire({ title: "Uploading Cover...", allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
    try {
      const blob = await getCroppedImg(coverSrc, coverCroppedPixels);
      const fd = new FormData();
      fd.append("file", blob, "cover.jpg");
      fd.append("username", user.username);
      const res = await fetch(`${config.API_BASE_URL}/Admin/upload-cover-picture`, { method: "POST", body: fd });
      if (!res.ok) { const e = await res.json(); throw new Error(e.message); }
      await Swal.fire({ icon: "success", title: "Cover Updated!", timer: 2000 });
      setShowCoverCrop(false);
      window.location.reload();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload Failed", text: err.message });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-gray-700 border-t-gray-300 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Loading profile...</p>
      </div>
    </div>
  );

  const avatarUrl = profilePicUrl ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullNames || user?.username || "U")}&size=200&background=1f2937&color=e5e7eb&bold=true`;

  return (
    <div className="min-h-screen bg-gray-950 pb-12">

      {/* COVER */}
      <div className="relative h-60 overflow-hidden group">
        {coverPicUrl
          ? <img src={coverPicUrl} className="w-full h-full object-cover" alt="Cover" />
          : <div className="w-full h-full bg-gradient-to-br from-gray-800 via-gray-750 to-gray-900" />
        }
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-950/80" />
        <label className="absolute bottom-3 right-4 px-3 py-1.5 bg-black/60 backdrop-blur text-gray-300 text-xs rounded-lg cursor-pointer hover:bg-black/80 transition-all flex items-center gap-1.5 opacity-0 group-hover:opacity-100 border border-gray-700">
          <Camera className="w-3.5 h-3.5" /> Change Cover
          <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
        </label>
      </div>

      {/* PROFILE CARD */}
      <div className="max-w-5xl mx-auto px-4 -mt-16 relative z-10">
        <div className="bg-gray-900 rounded-2xl border border-gray-800 shadow-2xl overflow-hidden">

          {/* Header */}
          <div className="px-8 pt-6 pb-5">
            <div className="flex flex-col md:flex-row items-center md:items-end gap-5">

              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <img src={avatarUrl} alt="Profile"
                  className="w-28 h-28 rounded-2xl border-4 border-gray-900 object-cover shadow-xl ring-2 ring-gray-700" />
                <label className="absolute bottom-1.5 right-1.5 p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer transition-all shadow">
                  <Camera className="w-3.5 h-3.5 text-gray-200" />
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => {
                      const f = e.target.files[0]; if (!f) return;
                      const r = new FileReader();
                      r.onload = () => setImageSrc(r.result);
                      r.readAsDataURL(f);
                      setShowEditModal(true);
                    }} />
                </label>
              </div>

              {/* Name + Badges */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl font-bold text-white">{user?.fullNames || user?.username}</h1>
                <p className="text-sm text-gray-500 mt-0.5">@{user?.username}</p>
                <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                  <span className="px-3 py-1 bg-gray-800 text-gray-300 border border-gray-700 rounded-full text-xs font-medium flex items-center gap-1.5">
                    <Shield className="w-3 h-3" />{user?.role}
                  </span>
                  <span className="px-3 py-1 bg-gray-800 text-gray-300 border border-gray-700 rounded-full text-xs font-medium flex items-center gap-1.5">
                    <Users className="w-3 h-3" />{user?.team || "No Team"}
                  </span>
                  {user?.isApproved
                    ? <span className="px-3 py-1 bg-green-950/50 text-green-400 border border-green-900 rounded-full text-xs font-medium flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3" />Approved
                      </span>
                    : <span className="px-3 py-1 bg-red-950/50 text-red-400 border border-red-900 rounded-full text-xs font-medium flex items-center gap-1.5">
                        <XCircle className="w-3 h-3" />Pending
                      </span>
                  }
                </div>
              </div>

              {/* Buttons */}
              {/* <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setShowEditModal(true)}
                  className="px-4 py-2 bg-gray-100 text-gray-900 text-sm rounded-xl hover:bg-white transition-all flex items-center gap-2 font-semibold">
                  <Edit2 className="w-3.5 h-3.5" />Edit Profile
                </button>
                <button onClick={() => setShowPasswordModal(true)}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-xl hover:bg-gray-700 transition-all flex items-center gap-2 font-medium">
                  <Lock className="w-3.5 h-3.5" />Password
                </button>
              </div> */}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-t border-gray-800 px-8">
            <div className="flex gap-6">
              {[
                { key: "about", label: "About", icon: <User className="w-3.5 h-3.5" /> },
                { key: "permissions", label: "Permissions", icon: <Shield className="w-3.5 h-3.5" /> },
                { key: "activity", label: "Activity", icon: <Clock className="w-3.5 h-3.5" /> },
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`py-3.5 border-b-2 text-sm font-medium transition-all flex items-center gap-1.5 ${
                    activeTab === tab.key
                      ? "border-gray-200 text-gray-100"
                      : "border-transparent text-gray-500 hover:text-gray-300"
                  }`}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === "about" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoCard icon={<Mail />} label="Email" value={user?.email} />
                <InfoCard icon={<Shield />} label="Role" value={user?.role} />
                <InfoCard icon={<Users />} label="Team" value={user?.team} />
                <InfoCard icon={<Award />} label="Role in Team" value={user?.roleTeams} />
                <InfoCard icon={<User />} label="ID Code" value={user?.identificationCode} />
                <InfoCard icon={<Calendar />} label="Registration Date"
                  value={user?.registrationDate ? new Date(user.registrationDate).toLocaleDateString() : "N/A"} />
                <InfoCard icon={<Clock />} label="Last Modified"
                  value={user?.modifiedDate ? new Date(user.modifiedDate).toLocaleDateString() : "N/A"} />
                <InfoCard
                  icon={user?.isApproved ? <CheckCircle /> : <XCircle />}
                  label="Account Status"
                  value={user?.isApproved ? "Approved" : "Pending"}
                  accent={user?.isApproved ? "green" : "red"}
                />
              </div>
            )}

            {activeTab === "permissions" && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  ["Chat", "chatPermission"], ["Notifications", "notificationPermission"],
                  ["Add User", "addUserPermission"], ["Add Admin", "addAdminPermission"],
                  ["Tasks", "taskPermission"], ["Incidents", "incidentPermission"],
                  ["Calendar", "calendarPermission"], ["Ticket List", "ticketlistPermission"],
                  ["Gallery", "galleryPermission"], ["Assignees", "assigneensPermission"],
                  ["Workload", "workloadPermission"], ["Email", "emailPermission"],
                  ["On-Call", "oncallPermission"],
                ].map(([label, key]) => <PermissionCard key={key} label={label} enabled={user?.[key]} />)}
              </div>
            )}

            {activeTab === "activity" && (
              <div className="text-center py-16">
                <div className="w-12 h-12 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-gray-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-400 mb-1">Activity Log</h3>
                <p className="text-sm text-gray-600">Activity tracking coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
        <Modal title="Edit Profile" onClose={() => { setShowEditModal(false); setImageSrc(null); }}>
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            {imageSrc ? (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-400">Crop Profile Picture</label>
                <div className="relative h-56 bg-black rounded-xl overflow-hidden">
                  <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1}
                    onCropChange={setCrop} onZoomChange={setZoom}
                    onCropComplete={(_, px) => setCroppedAreaPixels(px)} />
                </div>
                <input type="range" min="1" max="3" step="0.1" value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-full accent-gray-400" />
              </div>
            ) : (
              <label className="block cursor-pointer">
                <span className="text-sm font-medium text-gray-400">Profile Picture</span>
                <div className="mt-2 flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-700 rounded-xl hover:border-gray-500 transition-colors">
                  <ImageIcon className="w-10 h-10 text-gray-600 mb-2" />
                  <p className="text-sm text-gray-500">
                    <span className="text-gray-300 font-medium">Upload a file</span> or drag and drop
                  </p>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => setImageSrc(r.result); r.readAsDataURL(f); }} />
                </div>
              </label>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: "Full Name", key: "fullNames", ph: "Enter full name" },
                { label: "Email", key: "email", ph: "Enter email", type: "email" },
                { label: "Team", key: "team", ph: "Enter team" },
                { label: "Role in Team", key: "roleTeams", ph: "Enter role" },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">{f.label}</label>
                  <input type={f.type || "text"} placeholder={f.ph}
                    value={editData[f.key] || ""}
                    onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 text-sm focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none placeholder-gray-600" />
                </div>
              ))}
            </div>

            <button onClick={handleSaveProfile}
              className="w-full py-2.5 bg-gray-100 hover:bg-white text-gray-900 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all">
              <Save className="w-4 h-4" />Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* COVER CROP MODAL */}
      {showCoverCrop && (
        <Modal title="Adjust Cover Photo" onClose={() => setShowCoverCrop(false)}>
          <div className="space-y-4">
            <div className="relative h-56 bg-black rounded-xl overflow-hidden">
              <Cropper image={coverSrc} crop={coverCrop} zoom={coverZoom} aspect={16 / 9}
                onCropChange={setCoverCrop} onZoomChange={setCoverZoom}
                onCropComplete={(_, px) => setCoverCroppedPixels(px)} />
            </div>
            <input type="range" min="1" max="3" step="0.1" value={coverZoom}
              onChange={(e) => setCoverZoom(parseFloat(e.target.value))} className="w-full accent-gray-400" />
            <button onClick={handleSaveCover}
              className="w-full py-2.5 bg-gray-100 hover:bg-white text-gray-900 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all">
              <Save className="w-4 h-4" />Save Cover Photo
            </button>
          </div>
        </Modal>
      )}

      {/* PASSWORD MODAL */}
      {showPasswordModal && (
        <Modal title="Change Password" onClose={() => setShowPasswordModal(false)}>
          <div className="space-y-4">
            {[
              { label: "New Password", val: newPassword, set: setNewPassword, ph: "Enter new password" },
              { label: "Confirm Password", val: confirmPassword, set: setConfirmPassword, ph: "Confirm password" },
            ].map(f => (
              <div key={f.label}>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">{f.label}</label>
                <input type="password" placeholder={f.ph} value={f.val}
                  onChange={(e) => f.set(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 text-sm focus:ring-2 focus:ring-gray-500 outline-none placeholder-gray-600" />
              </div>
            ))}
            <button onClick={handleChangePassword}
              className="w-full py-2.5 bg-gray-100 hover:bg-white text-gray-900 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all">
              <Lock className="w-4 h-4" />Change Password
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-gray-900 rounded-2xl w-full max-w-2xl border border-gray-800 shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <h3 className="text-base font-bold text-gray-100">{title}</h3>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const InfoCard = ({ icon, label, value, accent }) => (
  <div className={`border rounded-xl p-4 ${
    accent === "green" ? "bg-green-950/30 border-green-900/60"
    : accent === "red" ? "bg-red-950/30 border-red-900/60"
    : "bg-gray-800/50 border-gray-800"
  }`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={accent === "green" ? "text-green-500" : accent === "red" ? "text-red-500" : "text-gray-500"}>
        {React.cloneElement(icon, { className: "w-4 h-4" })}
      </div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
    <p className={`text-sm font-semibold truncate ${
      accent === "green" ? "text-green-400" : accent === "red" ? "text-red-400" : "text-gray-100"
    }`}>{value || "—"}</p>
  </div>
);

const PermissionCard = ({ label, enabled }) => (
  <div className={`p-3.5 rounded-xl border transition-all ${
    enabled ? "bg-green-950/30 border-green-900/50" : "bg-gray-800/40 border-gray-800"
  }`}>
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-300">{label}</span>
      {enabled
        ? <CheckCircle className="w-4 h-4 text-green-500" />
        : <XCircle className="w-4 h-4 text-gray-600" />
      }
    </div>
  </div>
);

export default ViewProfiles;